/**
 * The two community program point layers: Firewise USA sites (national) and
 * California fire safe councils (statewide). Shared between the web
 * renderers (useCommunityProgramOverlays.web.ts), the map layers sheet and
 * MapLegend, so the glyph on the map and the swatch in the key can never
 * drift apart, the same arrangement fhszColors.ts and fireHistoryColors.ts
 * have.
 *
 * These are the only two map layers that are not scoped to a service area,
 * which is why each has one fixed URL instead of an {areaId} template.
 *
 * Both draw the same hexagon in the same teal, because they are the same
 * kind of thing: a community recognized for wildfire preparation. A one
 * letter corner badge carries which program, which is the map's badge
 * channel doing what it already does for before/after pairs.
 */

export type CommunityProgramId = 'firewise' | 'fire-safe-councils';

export interface CommunityProgramDef {
  id: CommunityProgramId;
  /** Row label in the layers sheet and the key. */
  label: string;
  /** Singular, for a popup heading. */
  singular: string;
  /** Static export written by scripts/export-*-geojson.ts. */
  url: string;
  /** Attribution line under the key, and in the popup footer. */
  sourceLabel: string;
  /** One letter in the marker's corner badge. */
  badge: string;
  /** Leaflet pane name and z-index. Separate panes: sharing one would mean
   *  hiding either layer hides both, which is what
   *  useFireHistoryOverlay's own comment warns about. Both sit under the
   *  Leaflet marker pane (600) so a property pin always wins a click. */
  pane: string;
  paneZIndex: string;
  /** Supercluster radius in pixels. The national layer buckets harder so
   *  the whole country does not draw 3,628 separate dots at zoom 3. */
  clusterRadius: number;
}

export const COMMUNITY_PROGRAMS: Record<CommunityProgramId, CommunityProgramDef> = {
  firewise: {
    id: 'firewise',
    label: 'Firewise USA sites',
    singular: 'Firewise USA site',
    url: '/firewise/national.geojson',
    sourceLabel: 'Sites in good standing, NFPA',
    badge: 'F',
    pane: 'firewisePane',
    paneZIndex: '585',
    clusterRadius: 70,
  },
  'fire-safe-councils': {
    id: 'fire-safe-councils',
    label: 'California fire safe councils',
    singular: 'California fire safe council',
    url: '/fire-safe-councils/california.geojson',
    sourceLabel: 'Wildfire resilience organizations, California Fire Safe Council',
    badge: 'C',
    pane: 'fireSafeCouncilsPane',
    paneZIndex: '590',
    clusterRadius: 55,
  },
};

export const COMMUNITY_PROGRAM_ORDER: CommunityProgramId[] = ['firewise', 'fire-safe-councils'];

/** Properties written by scripts/export-firewise-geojson.ts. */
export interface FirewiseProperties {
  name: string;
  county: string | null;
  city: string | null;
  state: string | null;
  approvalYear: number | null;
  residentCount: number | null;
  totalInvestment: number | null;
}

/** Properties written by scripts/export-fire-safe-councils-geojson.ts. */
export interface FireSafeCouncilProperties {
  name: string;
  orgClass: string | null;
  orgType: string | null;
  county: string | null;
  city: string | null;
  website: string | null;
}

export type CommunityProgramProperties = FirewiseProperties | FireSafeCouncilProperties;
